import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { useSelector, useDispatch } from 'react-redux'
import lang from '@/lang'
import Topbar from '@/modules/base/components/topbar/topbar'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { Backdrop } from '@mui/material'

import PlusIcon from '@assets/images/apps/plus.svg'
import Loader from '@/modules/base/components/loader/loader'

import {
  fetchDeveloper,
  fetchShowApp,
  installApp,
  uninstallApp,
  fetchShowAppStatus,
} from 'redux/ducks/apps'
import { withAsyncData } from '@/utils/withAsyncData'
import { useNavigate } from 'react-router'
import Carousel from '@/modules/base/components/carousel/carousel'
import { FreeMode, Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

const useStyles = makeStyles(theme => ({
  toolbar: {
    color: theme.textLight,
    backgroundColor: theme.listHeaderBackgroundColor,
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 40px 40px',
    background: theme.listHeaderBackgroundColor,
    borderBottom: `1px solid ${theme.borderColor}`,
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: 70,
    maxWidth: 70,
    marginRight: 20,
  },
  details: {
    display: 'flex',
    justifyContent: 'space-between',
    marginRight: 40,
  },
  appName: {
    fontSize: 28,
    lineHeight: '26px',
    marginTop: 0,
    margin: 0,
  },
  summary: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.textLight,
    margin: '8px 0',
  },
  developer: {
    margin: 0,
  },
  body: {
    padding: 40,
    display: 'flex',
    justifyContent: 'space-between',
  },
  slideshow: {
    width: 590,
    padding: '0 24px',
    marginRight: 40,
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  mainSwiper: {
    width: '100%',
    padding: 16,
    boxShadow: `0 0 10px 0 ${theme.borderColor}`,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: 4,
    backgroundColor: theme.white,
  },
  thumbNavigation: {
    marginTop: 10,
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    '& .swiper-wrapper': {
      display: 'flex',
    },
    '& .swiper-slide': {
      flexShrink: 0,
      cursor: 'pointer',
      transition: 'opacity 0.2s, border-color 0.2s',
      border: `2px solid ${theme.borderColor}`,
      borderRadius: 4,
    },
    '& .swiper-slide-thumb-active': {
      opacity: 1,
      borderColor: theme.primary,
    },
    '& .swiper-slide img': {
      width: '100%',
      height: 70,
      objectFit: 'cover',
      borderRadius: 4,
      display: 'block',
    },
  },
  title: {
    fontSize: 20,
    marginBottom: 20,
  },
  links: {
    display: 'flex',
    fontWeight: 'bold',
    margin: '20px 0',
    '& a': {
      color: theme.textColor,
      marginRight: 20,
    },
  },
  lightboxBackdrop: {
    zIndex: 1300,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
  },
  lightboxContainer: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  bigSwiperWrapper: {
    width: '80vw',
    maxWidth: 1000,
    display: 'flex',
    flexDirection: 'column-reverse',
    animation: '$expandFromPosition 350ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
    transformOrigin: 'top left',
  },
  bigMainSwiper: {
    width: '100%',
    backgroundColor: theme.white,
    padding: 16,
    borderRadius: 8,
    '& img': {
      maxHeight: '70vh',
      objectFit: 'contain',
    },
  },
  bigThumbNavigation: {
    marginTop: 16,
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    '& .swiper-wrapper': {
      display: 'flex',
    },
    '& .swiper-slide': {
      flexShrink: 0,
      cursor: 'pointer',
      opacity: 0.6,
      transition: 'opacity 0.2s',
      border: `2px solid ${theme.borderColor}`,
      borderRadius: 4,
    },
    '& .swiper-slide-thumb-active': {
      opacity: 1,
      borderColor: theme.primary,
    },
    '& .swiper-slide img': {
      width: '100%',
      height: 80,
      objectFit: 'cover',
      borderRadius: 4,
      display: 'block',
    },
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@keyframes expandFromPosition': {
    from: {
      transform: 'translate(var(--start-x, 0), var(--start-y, 0)) scale(var(--start-scale, 0.5))',
      opacity: 0,
    },
    to: {
      transform: 'translate(0, 0) scale(1)',
      opacity: 1,
    },
  },
}))

const AppDetail = ({ id, filter }) => {
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useDispatch()

  const { showApp, developers, loadingMessage } = useSelector(state => state.apps)
  const [developer, setDeveloper] = useState()

  const swiperContainerRef = useRef(null)
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const [showBigSwiper, setShowBigSwiper] = useState(false)
  const [bigThumbsSwiper, setBigThumbsSwiper] = useState(null)
  const [swiperPosition, setSwiperPosition] = useState(null)

  useEffect(() => {
    if (showApp.appId !== id) dispatch(fetchShowApp(id))
  }, [id])

  useEffect(() => {
    if (showApp.appId === id && showApp.isInstalled === null) dispatch(fetchShowAppStatus(showApp.appId))
  }, [showApp])

  useEffect(() => {
    if (showApp.developerId && !developers[showApp.developerId]) dispatch(fetchDeveloper(showApp.developerId))
    else if (developers[showApp.developerId]) setDeveloper(developers[showApp.developerId])
  }, [developers, showApp.developerId])

  const detailLang = lang.marketplace.detail

  const images = useMemo(() => {
    if (!showApp.images) return []
    return [...showApp.images]
  }, [showApp.images])

  const handleSlideClick = useCallback(() => {
    if (swiperContainerRef.current) {
      const rect = swiperContainerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const targetWidth = Math.min(viewportWidth * 0.8, 1000)
      const scale = rect.width / targetWidth

      const targetX = (viewportWidth - targetWidth) / 2
      const targetY = (viewportHeight - targetWidth * 0.6) / 2
      const startX = rect.left - targetX
      const startY = rect.top - targetY

      setSwiperPosition({ startX, startY, scale })
    }
    setShowBigSwiper(true)
  }, [])

  const handleCloseLightbox = useCallback(() => {
    setShowBigSwiper(false)
    setBigThumbsSwiper(null)
  }, [])

  const handleSlideChange = useCallback((swiper) => {
    setActiveIndex(swiper.realIndex)
  }, [])

  const thumbsConfig = useMemo(() => ({
    swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
  }), [thumbsSwiper])

  const bigThumbsConfig = useMemo(() => ({
    swiper: bigThumbsSwiper && !bigThumbsSwiper.destroyed ? bigThumbsSwiper : null,
  }), [bigThumbsSwiper])

  // Small swiper for the page
  const renderSmallSwiper = () => (
    <div className={classes.slideshow} ref={swiperContainerRef}>
      {/* Important order: thumbsSwiper must be before mainSwiper in the DOM */}
      <Swiper
        className={classes.thumbNavigation}
        modules={[FreeMode, Thumbs]}
        onSwiper={setThumbsSwiper}
        spaceBetween={10}
        slidesPerView={4}
        freeMode={true}
        watchSlidesProgress={true}
      >
        {images.map((s, idx) => (
          <SwiperSlide key={idx}>
            <img src={s} loading="lazy" alt="" />
          </SwiperSlide>
        ))}
      </Swiper>
      <Carousel
        customClass={classes.mainSwiper}
        modules={[FreeMode, Thumbs]}
        thumbs={thumbsConfig}
        infinite={true}
        settings={{ onSlideChange: handleSlideChange }}
      >
        {images.map((s, idx) => (
          <img key={idx} src={s} loading="lazy" alt="" style={{ width: '100%', cursor: 'pointer' }} onClick={handleSlideClick} />
        ))}
      </Carousel>
    </div>
  )

  const handleStopPropagation = useCallback((e) => e.stopPropagation(), [])

  // Big swiper for the lightbox
  const renderBigSwiper = () => {
    if (!showBigSwiper) return null

    const animationStyle = swiperPosition
      ? {
        '--start-x': `${swiperPosition.startX}px`,
        '--start-y': `${swiperPosition.startY}px`,
        '--start-scale': swiperPosition.scale,
      }
      : {}

    return (
      <Backdrop
        open={showBigSwiper}
        onClick={handleCloseLightbox}
        className={classes.lightboxBackdrop}
      >
        <div className={classes.lightboxContainer} onClick={handleCloseLightbox}>
          <div
            className={classes.bigSwiperWrapper}
            style={animationStyle}
            onClick={handleStopPropagation}
          >
            {/* Thumbs first in DOM for proper initialization */}
            <Swiper
              className={classes.bigThumbNavigation}
              modules={[FreeMode, Thumbs]}
              onSwiper={setBigThumbsSwiper}
              spaceBetween={10}
              slidesPerView={6}
              freeMode={true}
              watchSlidesProgress={true}
            >
              {images.map((s, idx) => (
                <SwiperSlide key={idx}>
                  <img src={s} loading="lazy" alt="" />
                </SwiperSlide>
              ))}
            </Swiper>
            <Carousel
              customClass={classes.bigMainSwiper}
              modules={[FreeMode, Thumbs]}
              thumbs={bigThumbsConfig}
              infinite={true}
              settings={{ initialSlide: activeIndex }}
            >
              {images.map((s, idx) => (
                <img key={idx} src={s} loading="lazy" alt="" style={{ width: '100%' }} />
              ))}
            </Carousel>
          </div>
        </div>
      </Backdrop>
    )
  }

  return (
    <>
      <div>
        <Topbar
          backButtonTitle={detailLang.toolbar}
          backButtonUrl={`/apps/${filter}`}
          size={'small'}
          className={classes.toolbar}
        />

        <div className={classes.head}>
          <div className={classes.details}>
            <div className={classes.imageContainer}>
              <img src={showApp.icon} />
            </div>
            <div>
              <p className={classes.appName}>{showApp.name}</p>
              <p className={classes.summary}>{showApp.summary}</p>
              <p className={classes.developer}>{developer?.name ? developer.name : ''}</p>
            </div>
          </div>
          {
            !showApp.isInstalled
              ? (
                <ButtonIconSquare icon={PlusIcon} onClick={() => {
                  dispatch(installApp(showApp))
                }}>{lang.marketplace.activateNow}</ButtonIconSquare>

              )
              : (
                <ButtonIconSquare onClick={() => showApp.isInternalApp
                  ? navigate(`${showApp.appOpenUrl}`)
                  : window.open(showApp.appOpenUrl, '_blank')}>{lang.marketplace.openApp}</ButtonIconSquare>
              )}
        </div>

        <div className={classes.body}>
          {!!loadingMessage && <Loader text={loadingMessage} visible={!!loadingMessage} />}
          {images && !!images.length && renderSmallSwiper()}
          <div>
            {showApp.description && (
              <>
                <div className={classes.title}>{detailLang.moreInformation}</div>
                <div dangerouslySetInnerHTML={{ __html: showApp.description }}></div>
              </>
            )}
            <div className={classes.links}>
              {developer?.email && (
                <a target="_blank" href={`mailto:${developer.email}`}>{lang.customerSupport}</a>
              )}
              {developer?.customData?.agbUrl && (
                <a target="_blank" href={developer.customData.agbUrl}>{lang.termsOfUse}</a>
              )}
              {developer?.customData?.website && (
                <a target="_blank" href={developer.customData.website}>{detailLang.website}</a>
              )}
            </div>
            {showApp.isInstalled && (
              <ButtonIconSquare variant="outline" color="red" onClick={() => dispatch(uninstallApp(showApp.appId))}>{lang.marketplace.removeApp}</ButtonIconSquare>
            )}
          </div>
        </div>
      </div>
      {renderBigSwiper()}
    </>
  )
}

AppDetail.propTypes = {
  id: PropTypes.string.isRequired,
  filter: PropTypes.string.isRequired,
}

const fetchData = async({ params }) => {
  const { filter, id } = params
  return Promise.resolve({ id, filter })
}

export default withAsyncData(AppDetail, 'Marktplatz', fetchData)
